import React, { useState, useEffect } from 'react';
import { DatabaseService, supabase } from '../../services/supabase';
import { DeckParser } from '../../services/deckParser';
import type { ParsedDeckCard } from '../../types/cards';

interface ContributorDetail {
  id: string;
  contributor_name: string;
  quantity: number;
  deck_filename: string;
  created_at: string;
}

interface DiscrepancyCard {
  cardName: string;
  databaseQuantity: number;
  collectionQuantity: number;
  shortage: number;
  contributors: ContributorDetail[];
  nonBkContributors: ContributorDetail[];
}

interface CorrectionAction {
  cardName: string;
  contributorId: string;
  contributorName: string;
  currentQuantity: number;
  newQuantity: number;
  action: 'reduce' | 'delete';
}

export const DatabaseCorrection: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collectionFile, setCollectionFile] = useState<File | null>(null);
  const [discrepancies, setDiscrepancies] = useState<DiscrepancyCard[]>([]);
  const [correctionActions, setCorrectionActions] = useState<CorrectionAction[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [executing, setExecuting] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCollectionFile(file);
      setError(null);
    }
  };

  const analyzeDiscrepancies = async () => {
    if (!collectionFile) {
      setError('Please select a collection file');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Parse the collection file
      const parsedDeck = await DeckParser.parseFile(collectionFile);
      
      if (parsedDeck.errors.length > 0) {
        setError(`File parsing errors: ${parsedDeck.errors.join(', ')}`);
        return;
      }

      // Get all gathered cards from database
      const gatheredCards = await DatabaseService.getGatheredCards();

      // Create collection map
      const collectionMap = new Map<string, number>();
      parsedDeck.cards.forEach(card => {
        collectionMap.set(card.name, card.quantity);
      });

      // Aggregate database quantities by card name and track contributors
      const databaseMap = new Map<string, number>();
      const contributorsMap = new Map<string, ContributorDetail[]>();
      
      gatheredCards.forEach(card => {
        const currentQuantity = databaseMap.get(card.card_name) || 0;
        databaseMap.set(card.card_name, currentQuantity + card.quantity);
        
        // Track contributors
        const contributors = contributorsMap.get(card.card_name) || [];
        contributors.push({
          id: card.id,
          contributor_name: card.contributor_name,
          quantity: card.quantity,
          deck_filename: card.deck_filename,
          created_at: card.created_at
        });
        contributorsMap.set(card.card_name, contributors);
      });

      // Find discrepancies (cards with more in DB than collection)
      const discrepancyCards: DiscrepancyCard[] = [];
      databaseMap.forEach((dbQuantity, cardName) => {
        const collectionQuantity = collectionMap.get(cardName) || 0;
        if (collectionQuantity < dbQuantity) {
          const contributors = contributorsMap.get(cardName) || [];
          const nonBkContributors = contributors.filter(c => 
            !c.contributor_name.toLowerCase().includes('bk') && 
            c.contributor_name.toLowerCase() !== 'bk'
          );
          
          discrepancyCards.push({
            cardName,
            databaseQuantity: dbQuantity,
            collectionQuantity,
            shortage: dbQuantity - collectionQuantity,
            contributors,
            nonBkContributors
          });
        }
      });

      // Sort by shortage (largest first)
      discrepancyCards.sort((a, b) => b.shortage - a.shortage);
      setDiscrepancies(discrepancyCards);

    } catch (err) {
      console.error('Analysis error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during analysis');
    } finally {
      setIsLoading(false);
    }
  };

  const generateCorrectionActions = () => {
    const actions: CorrectionAction[] = [];

    discrepancies.forEach(card => {
      let remainingShortage = card.shortage;
      
      // Sort non-BK contributors by quantity (largest first)
      const sortedNonBkContributors = [...card.nonBkContributors].sort((a, b) => b.quantity - a.quantity);
      
      for (const contributor of sortedNonBkContributors) {
        if (remainingShortage <= 0) break;
        
        const reductionAmount = Math.min(remainingShortage, contributor.quantity);
        const newQuantity = contributor.quantity - reductionAmount;
        
        actions.push({
          cardName: card.cardName,
          contributorId: contributor.id,
          contributorName: contributor.contributor_name,
          currentQuantity: contributor.quantity,
          newQuantity,
          action: newQuantity === 0 ? 'delete' : 'reduce'
        });
        
        remainingShortage -= reductionAmount;
      }
    });

    setCorrectionActions(actions);
    setShowPreview(true);
  };

  const executeCorrectionActions = async () => {
    setExecuting(true);
    setError(null);

    try {
      for (const action of correctionActions) {
        if (action.action === 'delete') {
          const { error } = await supabase
            .from('gathered_cards')
            .delete()
            .eq('id', action.contributorId);
          
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('gathered_cards')
            .update({ quantity: action.newQuantity })
            .eq('id', action.contributorId);
          
          if (error) throw error;
        }
      }

      setError('Successfully applied all corrections!');
      setShowPreview(false);
      setCorrectionActions([]);
      
      // Re-analyze to confirm changes
      await analyzeDiscrepancies();

    } catch (err) {
      console.error('Execution error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during execution');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">Database Correction Tool</h2>
      
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select MTGO Collection File (.dek)
          </label>
          <input
            type="file"
            accept=".dek"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
          />
        </div>

        <div className="flex gap-4">
          <button
            onClick={analyzeDiscrepancies}
            disabled={!collectionFile || isLoading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            {isLoading ? 'Analyzing...' : 'Analyze Discrepancies'}
          </button>
          
          {discrepancies.length > 0 && !showPreview && (
            <button
              onClick={generateCorrectionActions}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Generate Correction Plan
            </button>
          )}
        </div>

        {error && (
          <div className={`mt-4 p-4 rounded border ${
            error.includes('Successfully') 
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {error}
          </div>
        )}
      </div>

      {/* Discrepancies Analysis */}
      {discrepancies.length > 0 && !showPreview && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            Found {discrepancies.length} cards with discrepancies
          </h3>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">Card Name</th>
                  <th className="px-4 py-2 text-center">DB Qty</th>
                  <th className="px-4 py-2 text-center">Collection Qty</th>
                  <th className="px-4 py-2 text-center">Shortage</th>
                  <th className="px-4 py-2 text-center">Non-BK Contributors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {discrepancies.map((card, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{card.cardName}</td>
                    <td className="px-4 py-2 text-center">{card.databaseQuantity}</td>
                    <td className="px-4 py-2 text-center">{card.collectionQuantity}</td>
                    <td className="px-4 py-2 text-center font-semibold text-red-600">
                      -{card.shortage}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {card.nonBkContributors.map(c => 
                        `${c.contributor_name} (${c.quantity})`
                      ).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Correction Actions Preview */}
      {showPreview && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            Correction Actions Preview ({correctionActions.length} actions)
          </h3>
          
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-yellow-800 font-medium">⚠️ Warning</p>
            <p className="text-yellow-700 text-sm mt-1">
              This will permanently modify the database. Review all actions carefully before proceeding.
            </p>
          </div>

          <div className="max-h-96 overflow-y-auto mb-4">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">Card Name</th>
                  <th className="px-4 py-2 text-left">Contributor</th>
                  <th className="px-4 py-2 text-center">Current Qty</th>
                  <th className="px-4 py-2 text-center">New Qty</th>
                  <th className="px-4 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {correctionActions.map((action, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{action.cardName}</td>
                    <td className="px-4 py-2">{action.contributorName}</td>
                    <td className="px-4 py-2 text-center">{action.currentQuantity}</td>
                    <td className="px-4 py-2 text-center">{action.newQuantity}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        action.action === 'delete' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {action.action === 'delete' ? 'DELETE' : 'REDUCE'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-4">
            <button
              onClick={executeCorrectionActions}
              disabled={executing}
              className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 disabled:bg-gray-400"
            >
              {executing ? 'Executing...' : 'Execute All Corrections'}
            </button>
            
            <button
              onClick={() => setShowPreview(false)}
              className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};